import OpenApiConfiguration, { API_CLIENT_HEADERS } from 'api/OpenApiConfiguration';
import axios, { AxiosError } from 'axios';
import { useMutation, UseMutationOptions } from 'react-query';
import {
  DomesticV2BookingsIdAboardPatchRequest,
  DomesticV2BookingsIdReviewsPostRequest,
  DomesticV2BookingsIdModificationsGetRequest,
  DomesticV2CicosIdPreGetRequest,
  DomesticV2CicosIdPostGetRequest,
  DomesticV2BookingsIdApprovalPostRequest,
  DomesticV2BookingsIdAssignCaptainPostRequest,
  DomesticV2BookingsIdCaptainDeclinePostRequest,
  DomesticV2BookingsIdCheckoutPostRequest,
  DomesticV2BookingsCheckoutPostRequest,
  DomesticV2MeCreditCardsGetRequest,
} from 'swagger/models';
import { BookingsApi } from 'swagger/apis/bookings-api';
import { CicoApi } from 'swagger/apis/cico-api';

const bookingsApi = new BookingsApi(OpenApiConfiguration);
const cicoApi = new CicoApi(OpenApiConfiguration);

export type MutationOptionsCustomType<ApiEndpointFnType extends (...args: any) => any, PayloadType> = Omit<
  UseMutationOptions<Awaited<ReturnType<ApiEndpointFnType>>, AxiosError<void, any>, PayloadType>,
  'mutationFn'
>;

type CanceBookingModificationMutationPayloadType = { id: string; modificationId: string };

export const useCancelBookingModificationMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdModificationsModificationIdDelete,
    CanceBookingModificationMutationPayloadType
  >
) =>
  useMutation(
    ({ id, modificationId }) => bookingsApi.domesticV2BookingsIdModificationsModificationIdDelete(id, modificationId),
    options
  );

type SetBookingAshoreMutationPayloadType = { bookingId: string; body?: DomesticV2BookingsIdAboardPatchRequest };

export const useSetBookingAshoreMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdAshorePatch,
    SetBookingAshoreMutationPayloadType
  >
) => useMutation(({ bookingId, body }) => bookingsApi.domesticV2BookingsIdAshorePatch(bookingId, body), options);

type SetBookingAbroadMutationPayloadType = { bookingId: string; body?: DomesticV2BookingsIdAboardPatchRequest };

export const useSetBookingAbroadMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdAboardPatch,
    SetBookingAbroadMutationPayloadType
  >
) => useMutation(({ bookingId, body }) => bookingsApi.domesticV2BookingsIdAboardPatch(bookingId, body), options);

type CreateReviewMutationPayloadType = { bookingId: string; body?: DomesticV2BookingsIdReviewsPostRequest };

export const useCreateReviewMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdReviewsPost,
    CreateReviewMutationPayloadType
  >
) => useMutation(({ bookingId, body }) => bookingsApi.domesticV2BookingsIdReviewsPost(bookingId, body), options);

type CreateBookingModificationMutationPayloadType = { id: string; body: DomesticV2BookingsIdModificationsGetRequest };

export const useCreateBookingModificationMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdModificationsPost,
    CreateBookingModificationMutationPayloadType
  >
) => useMutation(({ id, body }) => bookingsApi.domesticV2BookingsIdModificationsPost(id, body), options);

type CanceBookingPreApprovalMutationPayloadType = { id: string };

export const useCancelBookingPreApprovalMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdPreApprovalDelete,
    CanceBookingPreApprovalMutationPayloadType
  >
) => useMutation(({ id }) => bookingsApi.domesticV2BookingsIdPreApprovalDelete(id), options);

type CreateBookingCheckInMutationPayloadType = { bookingId: string; cicoRequest?: DomesticV2CicosIdPreGetRequest };

export const useCreateBookingCheckInMutation = (
  options?: MutationOptionsCustomType<typeof cicoApi.domesticV2CicosIdPrePost, CreateBookingCheckInMutationPayloadType>
) => useMutation(({ bookingId, cicoRequest }) => cicoApi.domesticV2CicosIdPrePost(bookingId, cicoRequest), options);

type CreateBookingCheckOutMutationPayloadType = { bookingId: string; cicoRequest?: DomesticV2CicosIdPostGetRequest };

export const useCreateBookingCheckOutMutation = (
  options?: MutationOptionsCustomType<
    typeof cicoApi.domesticV2CicosIdPostPost,
    CreateBookingCheckOutMutationPayloadType
  >
) => useMutation(({ bookingId, cicoRequest }) => cicoApi.domesticV2CicosIdPostPost(bookingId, cicoRequest), options);

type CreatePassengerShortCodeMutationPayloadType = { id: string };

export const useCreatePassengerShortCodeMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdPassengerShortCodesPost,
    CreatePassengerShortCodeMutationPayloadType
  >
) => useMutation(({ id }) => bookingsApi.domesticV2BookingsIdPassengerShortCodesPost(id), options);

type CreateCheckoutWithBookingMutationPayloadType = {
  bookingId: string;
  body: DomesticV2BookingsIdCheckoutPostRequest;
};

export const useCreateCheckoutWithBookingMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdCheckoutPost,
    CreateCheckoutWithBookingMutationPayloadType
  >
) =>
  useMutation(
    ({ bookingId, body }: CreateCheckoutWithBookingMutationPayloadType) =>
      bookingsApi.domesticV2BookingsIdCheckoutPost(bookingId, body),
    options
  );

type CreateCheckoutMutationPayloadType = { body: DomesticV2BookingsCheckoutPostRequest };

export const useCreateCreditCardMutation = () =>
  useMutation((body: DomesticV2MeCreditCardsGetRequest) =>
    axios.post('/domestic/v1/me/credit_cards', body, {
      headers: API_CLIENT_HEADERS,
    })
  );

export const useCreateCheckoutMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsCheckoutPost,
    CreateCheckoutMutationPayloadType
  >
) =>
  useMutation(
    ({ body }: CreateCheckoutMutationPayloadType) => bookingsApi.domesticV2BookingsCheckoutPost(body),
    options
  );

export const useDeleteBookingMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdDelete,
    { bookingId: string; category?: string; reasonText?: string }
  >
) =>
  useMutation(
    ({ bookingId, category, reasonText }) => bookingsApi.domesticV2BookingsIdDelete(bookingId, category, reasonText),
    options
  );

export const useApproveBookingMutation = (
  options: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdApprovalPost,
    { bookingId: string; body?: DomesticV2BookingsIdApprovalPostRequest }
  >
) => useMutation(({ bookingId, body }) => bookingsApi.domesticV2BookingsIdApprovalPost(bookingId, body), options);

export const usePreApproveBookingMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdPreApprovalPost,
    { bookingId: string; body: DomesticV2BookingsIdApprovalPostRequest }
  >
) => useMutation(({ bookingId, body }) => bookingsApi.domesticV2BookingsIdPreApprovalPost(bookingId, body), options);

export const useAssignCaptainMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdAssignCaptainPost,
    { bookingId: string; body: DomesticV2BookingsIdAssignCaptainPostRequest }
  >
) => useMutation(({ bookingId, body }) => bookingsApi.domesticV2BookingsIdAssignCaptainPost(bookingId, body), options);

export const useApproveCaptainForBookingMutation = (
  options?: MutationOptionsCustomType<typeof bookingsApi.domesticV2BookingsIdCaptainApprovePost, { bookingId: string }>
) => useMutation(({ bookingId }) => bookingsApi.domesticV2BookingsIdCaptainApprovePost(bookingId), options);

export const useDeclineCaptainForBookingMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdCaptainDeclinePost,
    { bookingId: string } & DomesticV2BookingsIdCaptainDeclinePostRequest
  >
) =>
  useMutation(({ bookingId, ...body }) => bookingsApi.domesticV2BookingsIdCaptainDeclinePost(bookingId, body), options);

export const useCancelCaptainForBookingMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdCaptainCancelPost,
    { bookingId: string } & DomesticV2BookingsIdCaptainDeclinePostRequest
  >
) =>
  useMutation(({ bookingId, ...body }) => bookingsApi.domesticV2BookingsIdCaptainCancelPost(bookingId, body), options);

export const useAcceptBookingModificationMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdModificationsModificationIdAcceptPost,
    { bookingId: string; modificationId: string }
  >
) =>
  useMutation(
    ({ bookingId, modificationId }) =>
      bookingsApi.domesticV2BookingsIdModificationsModificationIdAcceptPost(bookingId, modificationId),
    options
  );

export const useDeclineBookingModificationMutation = (
  options?: MutationOptionsCustomType<
    typeof bookingsApi.domesticV2BookingsIdModificationsModificationIdDeclinePost,
    { bookingId: string; modificationId: string }
  >
) =>
  useMutation(
    ({ bookingId, modificationId }) =>
      bookingsApi.domesticV2BookingsIdModificationsModificationIdDeclinePost(bookingId, modificationId),
    options
  );
